/**
 * render/quest_ui.ts — Quest & Dialog UI Rendering
 *
 * Beautiful, warm UI for quest interactions:
 *   - Dialog panel: NPC portrait area + text + option buttons + free text input
 *   - Quest giver marks: Floating "!" and "?" above NPC heads
 *   - Quest tracker: Pinned objectives on the right side of screen
 *   - Quest log: Full-screen panel with quest details
 *   - Quest accept/complete overlay: Celebration animation
 *
 * Visual language: warm amber + parchment aesthetic, consistent with HUD.
 */

import type { GameState, QuestGiverMark, DialogOption } from "../engine/state.js";
import { QUEST_INDEX } from "../narrative/quests.js";

// ─── Colors ─────────────────────────────────────────────────────────────────

const PARCHMENT      = "#f5e6c8";
const PARCHMENT_DARK = "#d4c4a0";
const INK            = "#2a1a08";
const INK_LIGHT      = "#5a4020";
const AMBER          = "#e8b040";
const AMBER_GLOW     = "#ffd060";
const SILVER          = "#a0a8b0";
const SILVER_LIGHT   = "#c8d0d8";
const GOLD_MARK      = "#ffd700";
const GREY_MARK      = "#808888";
const GREEN_COMPLETE = "#40c060";
const RED_LOCKED     = "#a04040";
const PANEL_BG       = "rgba(20, 14, 8, 0.92)";
const PANEL_BORDER   = "#8a6a3a";

// ─── Quest Giver Marks ──────────────────────────────────────────────────────

/**
 * Draw a floating quest marker above an NPC at the given screen position.
 * The marker bobs gently and has a subtle glow.
 */
export function drawQuestGiverMark(
  ctx: CanvasRenderingContext2D,
  mark: QuestGiverMark,
  screenX: number,
  screenY: number,
  now: number,
): void {
  if (!mark) return;

  const bob = Math.sin(now * 2.0) * 3;
  const mx = screenX;
  const my = screenY - 8 + bob;

  let glyph: string;
  let glyphColor: string;
  let glowColor: string;
  let glowSize: number;

  switch (mark) {
    case "available":
      glyph = "!";
      glyphColor = GOLD_MARK;
      glowColor = "rgba(255, 215, 0, 0.4)";
      glowSize = 14;
      break;
    case "ready_complete":
      glyph = "?";
      glyphColor = GOLD_MARK;
      glowColor = "rgba(255, 215, 0, 0.5)";
      glowSize = 14;
      break;
    case "in_progress":
      glyph = "?";
      glyphColor = SILVER;
      glowColor = "rgba(160, 168, 176, 0.3)";
      glowSize = 12;
      break;
    case "unavailable":
      glyph = "!";
      glyphColor = GREY_MARK;
      glowColor = "rgba(128, 136, 136, 0.2)";
      glowSize = 10;
      break;
    default:
      return;
  }

  ctx.save();

  // Glow circle
  const pulse = 0.8 + Math.sin(now * 3.0) * 0.2;
  const grad = ctx.createRadialGradient(mx, my, 0, mx, my, glowSize * pulse);
  grad.addColorStop(0, glowColor);
  grad.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(mx, my, glowSize * pulse, 0, Math.PI * 2);
  ctx.fill();

  // Shadow
  ctx.font = "bold 18px 'Georgia', serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "rgba(0,0,0,0.5)";
  ctx.fillText(glyph, mx + 1, my + 1);

  // Glyph
  ctx.fillStyle = glyphColor;
  ctx.fillText(glyph, mx, my);

  // Extra sparkle for gold marks — pillar of light + rising particles
  if (mark === "available" || mark === "ready_complete") {
    const sparkle = Math.sin(now * 5.0) * 0.5 + 0.5;
    ctx.globalAlpha = sparkle * 0.6;
    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 10px 'Georgia', serif";
    ctx.fillText("✦", mx + 8, my - 6);

    // Pillar of light — soft vertical beam from ground to mark
    const pillarH = 60;
    const pillarW = 6;
    const pillarGrad = ctx.createLinearGradient(mx, my + 20, mx, my - pillarH);
    pillarGrad.addColorStop(0, "rgba(255, 215, 0, 0)");
    pillarGrad.addColorStop(0.3, "rgba(255, 215, 0, 0.08)");
    pillarGrad.addColorStop(0.7, "rgba(255, 215, 0, 0.12)");
    pillarGrad.addColorStop(1, "rgba(255, 215, 0, 0)");
    ctx.globalAlpha = 0.6 + sparkle * 0.4;
    ctx.fillStyle = pillarGrad;
    ctx.fillRect(mx - pillarW / 2, my - pillarH, pillarW, pillarH + 20);

    // Rising sparkle particles — 5 particles at staggered phases
    for (let i = 0; i < 5; i++) {
      const phase = (now * 0.8 + i * 0.63) % 1; // 0→1 loop
      const py = my + 16 - phase * (pillarH + 20);
      const px2 = mx + Math.sin(now * 2.3 + i * 1.7) * 4;
      const alpha = phase < 0.1 ? phase / 0.1 : phase > 0.8 ? (1 - phase) / 0.2 : 1;
      ctx.globalAlpha = alpha * 0.7;
      ctx.fillStyle = i % 2 === 0 ? "#ffd700" : "#ffffff";
      ctx.beginPath();
      ctx.arc(px2, py, 1.2, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.globalAlpha = 1;
  }

  ctx.restore();
}

// ─── Dialog Panel ───────────────────────────────────────────────────────────

const DIALOG_W = 480;
const DIALOG_H = 340;
const DIALOG_PAD = 20;
const OPTION_H = 36;
const OPTION_GAP = 4;
const INPUT_H = 32;

/** Hit zones for dialog options — rebuilt each frame */
export const dialogHitZones: Array<{
  x: number; y: number; w: number; h: number; optionId: string;
}> = [];

/** Hit zone for the free text input area */
export let dialogInputHitZone: { x: number; y: number; w: number; h: number } | null = null;

/** Full dialog panel bounds — for click-blocking (nothing behind the panel is clickable) */
export let dialogPanelBounds: { x: number; y: number; w: number; h: number } | null = null;

/**
 * Draw the NPC dialog panel — centered at the bottom of the screen.
 * Shows NPC name/title, dialog text, option buttons, and free text input.
 */
export function drawDialogPanel(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  now: number,
): void {
  const d = state.dialog;
  if (!d.open) {
    dialogHitZones.length = 0;
    dialogInputHitZone = null;
    dialogPanelBounds = null;
    return;
  }

  const cw = state.canvasWidth;
  const ch = state.canvasHeight;

  // Slide-up animation
  const age = now - d.openedAt;
  const slideT = Math.min(1, age / 0.3);
  const ease = 1 - Math.pow(1 - slideT, 3); // ease-out cubic
  const slideOffset = (1 - ease) * DIALOG_H;

  const px = Math.round((cw - DIALOG_W) / 2);
  const py = Math.round(ch - DIALOG_H - 80 + slideOffset);

  // Set panel bounds for click blocking
  dialogPanelBounds = { x: px, y: py, w: DIALOG_W, h: DIALOG_H };

  ctx.save();
  ctx.globalAlpha = ease;

  // Clear hit zones
  dialogHitZones.length = 0;
  dialogInputHitZone = null;

  // ── Panel background ─────────────────────────────────────────────
  // Outer glow
  ctx.shadowColor = "rgba(232, 176, 64, 0.3)";
  ctx.shadowBlur = 20;
  roundRect(ctx, px - 2, py - 2, DIALOG_W + 4, DIALOG_H + 4, 12);
  ctx.fillStyle = PANEL_BORDER;
  ctx.fill();
  ctx.shadowBlur = 0;

  // Inner panel
  roundRect(ctx, px, py, DIALOG_W, DIALOG_H, 10);
  ctx.fillStyle = PANEL_BG;
  ctx.fill();
  ctx.strokeStyle = PANEL_BORDER;
  ctx.lineWidth = 2;
  ctx.stroke();

  // ── NPC name + title ─────────────────────────────────────────────
  const nameY = py + DIALOG_PAD;
  ctx.font = "bold 16px 'Georgia', serif";
  ctx.fillStyle = AMBER;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText(d.npcName ?? "???", px + DIALOG_PAD, nameY);

  if (d.npcTitle) {
    ctx.font = "italic 12px 'Georgia', serif";
    ctx.fillStyle = PARCHMENT_DARK;
    ctx.fillText(d.npcTitle, px + DIALOG_PAD, nameY + 20);
  }

  // Divider line under name
  const divY = nameY + 38;
  ctx.beginPath();
  ctx.moveTo(px + DIALOG_PAD, divY);
  ctx.lineTo(px + DIALOG_W - DIALOG_PAD, divY);
  ctx.strokeStyle = "rgba(138, 106, 58, 0.4)";
  ctx.lineWidth = 1;
  ctx.stroke();

  // ── Dialog text ──────────────────────────────────────────────────
  const textY = divY + 10;
  const textMaxW = DIALOG_W - DIALOG_PAD * 2;
  ctx.font = "14px 'Georgia', serif";
  ctx.fillStyle = PARCHMENT;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";

  // Word-wrap text
  const lines = wrapText(ctx, d.text, textMaxW);
  const maxTextLines = 5;
  const visibleLines = lines.slice(0, maxTextLines);
  for (let i = 0; i < visibleLines.length; i++) {
    ctx.fillText(visibleLines[i], px + DIALOG_PAD, textY + i * 18);
  }

  // ── Options ──────────────────────────────────────────────────────
  const optionsY = textY + maxTextLines * 18 + 12;
  const optionW = DIALOG_W - DIALOG_PAD * 2;
  const maxVisibleOptions = 4;
  const visibleOptions = d.options.slice(0, maxVisibleOptions);

  for (let i = 0; i < visibleOptions.length; i++) {
    const opt = visibleOptions[i];
    const ox = px + DIALOG_PAD;
    const oy = optionsY + i * (OPTION_H + OPTION_GAP);

    // Hit zone
    dialogHitZones.push({ x: ox, y: oy, w: optionW, h: OPTION_H, optionId: opt.id });

    // Option background
    const isHovered = d.hoveredOption === i;
    const isDisabled = opt.disabled;

    roundRect(ctx, ox, oy, optionW, OPTION_H, 6);
    if (isDisabled) {
      ctx.fillStyle = "rgba(60, 50, 40, 0.4)";
    } else if (isHovered) {
      ctx.fillStyle = "rgba(232, 176, 64, 0.2)";
    } else {
      ctx.fillStyle = "rgba(80, 60, 30, 0.3)";
    }
    ctx.fill();
    ctx.strokeStyle = isHovered && !isDisabled ? AMBER : "rgba(138, 106, 58, 0.3)";
    ctx.lineWidth = 1;
    ctx.stroke();

    // Option number
    ctx.font = "bold 13px 'Georgia', serif";
    ctx.fillStyle = isDisabled ? GREY_MARK : AMBER;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(`${i + 1}.`, ox + 10, oy + OPTION_H / 2);

    // Option text
    ctx.font = isDisabled ? "italic 13px 'Georgia', serif" : "13px 'Georgia', serif";
    ctx.fillStyle = isDisabled ? GREY_MARK : PARCHMENT;
    ctx.fillText(opt.text, ox + 30, oy + OPTION_H / 2);

    // Quest action icon
    if (opt.action === "accept_quest") {
      ctx.fillStyle = GOLD_MARK;
      ctx.font = "bold 14px 'Georgia', serif";
      ctx.textAlign = "right";
      ctx.fillText("!", ox + optionW - 12, oy + OPTION_H / 2);
    } else if (opt.action === "complete_quest") {
      ctx.fillStyle = GREEN_COMPLETE;
      ctx.font = "bold 14px 'Georgia', serif";
      ctx.textAlign = "right";
      ctx.fillText("✓", ox + optionW - 12, oy + OPTION_H / 2);
    }
  }

  // ── Free text input ──────────────────────────────────────────────
  if (d.showFreeInput) {
    const inputY = py + DIALOG_H - DIALOG_PAD - INPUT_H;
    const inputX = px + DIALOG_PAD;
    const inputW = optionW;

    dialogInputHitZone = { x: inputX, y: inputY, w: inputW, h: INPUT_H };

    roundRect(ctx, inputX, inputY, inputW, INPUT_H, 4);
    ctx.fillStyle = "rgba(40, 30, 18, 0.6)";
    ctx.fill();
    ctx.strokeStyle = "rgba(138, 106, 58, 0.4)";
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.font = "13px 'Georgia', serif";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";

    if (d.freeInputValue) {
      ctx.fillStyle = PARCHMENT;
      ctx.fillText(d.freeInputValue, inputX + 10, inputY + INPUT_H / 2);
    } else {
      ctx.fillStyle = "rgba(200, 180, 140, 0.4)";
      ctx.fillText("Type a response... (Enter to send)", inputX + 10, inputY + INPUT_H / 2);
    }

    // Blinking cursor
    if (Math.floor(now * 2) % 2 === 0) {
      const cursorX = inputX + 10 + ctx.measureText(d.freeInputValue || "").width;
      ctx.fillStyle = AMBER;
      ctx.fillRect(cursorX + 2, inputY + 8, 1, INPUT_H - 16);
    }
  }

  // ── Keyboard hints ───────────────────────────────────────────────
  ctx.font = "10px 'Georgia', serif";
  ctx.fillStyle = "rgba(200, 180, 140, 0.3)";
  ctx.textAlign = "right";
  ctx.textBaseline = "bottom";
  ctx.fillText("ESC to close  •  1-4 to select", px + DIALOG_W - DIALOG_PAD, py + DIALOG_H - 6);

  ctx.restore();
}

// ─── Quest Tracker (HUD overlay, right side) ────────────────────────────────

const TRACKER_W = 220;
const TRACKER_PAD = 10;
const TRACKER_STEP_H = 16;

/**
 * Draw the quest tracker on the right side of the screen.
 * Shows pinned quests with progress indicators.
 */
export function drawQuestTracker(
  ctx: CanvasRenderingContext2D,
  state: GameState,
): void {
  const tracked = state.questUI.trackedQuestIds;
  if (tracked.length === 0) return;

  const cw = state.canvasWidth;
  // Position below minimap circle (R=65, top=16 → bottom=146) + room name + margin
  const baseX = cw - TRACKER_W - 12;
  let y = 170;

  ctx.save();

  // Arc color accent map
  const arcColors: Record<string, string> = {
    social: "#60b0e0", creative: "#e0a040", exploration: "#60c070",
    stewardship: "#a0d060", consciousness: "#c080e0",
  };

  for (const qid of tracked) {
    const quest = QUEST_INDEX.get(qid);
    const progress = state.player.quests.find(q => q.questId === qid);
    if (!quest || !progress || progress.status === "complete") continue;

    const stepCount = quest.steps.length;
    const doneCount = progress.completedSteps.length;
    const panelH = 22 + stepCount * TRACKER_STEP_H + 6;
    const arcCol = arcColors[quest.arc] ?? AMBER;

    // Panel background
    roundRect(ctx, baseX - 6, y - 4, TRACKER_W + 12, panelH, 6);
    ctx.fillStyle = "rgba(14, 10, 6, 0.82)";
    ctx.fill();
    ctx.strokeStyle = "rgba(100, 80, 40, 0.3)";
    ctx.lineWidth = 0.5;
    ctx.stroke();

    // Arc-colored left accent bar
    ctx.fillStyle = arcCol;
    ctx.fillRect(baseX - 4, y, 2, panelH - 8);

    // Quest name
    ctx.font = "bold 11px 'Georgia', serif";
    ctx.fillStyle = AMBER;
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText(quest.name, baseX + 2, y);

    // Progress fraction — right-aligned
    ctx.font = "9px 'Georgia', serif";
    ctx.fillStyle = "rgba(200,180,140,0.45)";
    ctx.textAlign = "right";
    ctx.fillText(`${doneCount}/${stepCount}`, baseX + TRACKER_W + 2, y + 1);

    y += 16;

    // Steps
    ctx.textAlign = "left";
    for (const step of quest.steps) {
      const done = progress.completedSteps.includes(step.id);
      ctx.font = "10px 'Georgia', serif";
      if (done) {
        ctx.fillStyle = GREEN_COMPLETE;
        ctx.fillText("✓ " + step.description, baseX + 8, y);
      } else {
        ctx.fillStyle = PARCHMENT_DARK;
        ctx.fillText("○ " + step.description, baseX + 8, y);
      }
      y += TRACKER_STEP_H;
    }

    y += 10; // gap between quests
  }

  ctx.restore();
}

// ─── Quest Log Panel ────────────────────────────────────────────────────────

/**
 * Draw the full quest log panel (toggled with L key).
 */
export function drawQuestLog(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  now: number,
): void {
  if (!state.questUI.logOpen) return;

  const cw = state.canvasWidth;
  const ch = state.canvasHeight;
  const panelW = Math.min(600, cw - 80);
  const panelH = Math.min(500, ch - 100);
  const px = Math.round((cw - panelW) / 2);
  const py = Math.round((ch - panelH) / 2);

  ctx.save();

  // Backdrop
  ctx.fillStyle = "rgba(0, 0, 0, 0.6)";
  ctx.fillRect(0, 0, cw, ch);

  // Panel
  roundRect(ctx, px - 2, py - 2, panelW + 4, panelH + 4, 12);
  ctx.fillStyle = PANEL_BORDER;
  ctx.fill();

  roundRect(ctx, px, py, panelW, panelH, 10);
  ctx.fillStyle = PANEL_BG;
  ctx.fill();

  // Title
  ctx.font = "bold 20px 'Georgia', serif";
  ctx.fillStyle = AMBER;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText("Quest Log", px + panelW / 2, py + 16);

  // Quest list (left side)
  const listW = Math.round(panelW * 0.4);
  const detailX = px + listW;
  let ly = py + 50;

  // Divider
  ctx.beginPath();
  ctx.moveTo(detailX, py + 46);
  ctx.lineTo(detailX, py + panelH - 16);
  ctx.strokeStyle = "rgba(138, 106, 58, 0.3)";
  ctx.lineWidth = 1;
  ctx.stroke();

  const activeQuests = state.player.quests.filter(q => q.status === "active");
  const completedQuests = state.player.quests.filter(q => q.status === "complete");

  // Active quests section
  ctx.font = "bold 11px 'Georgia', serif";
  ctx.fillStyle = AMBER_GLOW;
  ctx.textAlign = "left";
  ctx.fillText("ACTIVE", px + 16, ly);
  ly += 18;

  for (const pq of activeQuests) {
    const quest = QUEST_INDEX.get(pq.questId);
    if (!quest) continue;

    const isSelected = state.questUI.selectedQuestId === pq.questId;
    if (isSelected) {
      roundRect(ctx, px + 8, ly - 2, listW - 16, 22, 4);
      ctx.fillStyle = "rgba(232, 176, 64, 0.15)";
      ctx.fill();
    }

    ctx.font = "13px 'Georgia', serif";
    ctx.fillStyle = isSelected ? AMBER : PARCHMENT;
    ctx.fillText(quest.name, px + 16, ly);
    ly += 24;
  }

  // Completed section
  if (completedQuests.length > 0) {
    ly += 12;
    ctx.font = "bold 11px 'Georgia', serif";
    ctx.fillStyle = GREEN_COMPLETE;
    ctx.fillText("COMPLETED", px + 16, ly);
    ly += 18;

    for (const pq of completedQuests.slice(0, 8)) {
      const quest = QUEST_INDEX.get(pq.questId);
      if (!quest) continue;

      ctx.font = "13px 'Georgia', serif";
      ctx.fillStyle = "rgba(200, 180, 140, 0.5)";
      ctx.fillText("✓ " + quest.name, px + 16, ly);
      ly += 22;
    }
  }

  // Detail panel (right side)
  const selectedId = state.questUI.selectedQuestId ?? activeQuests[0]?.questId;
  if (selectedId) {
    const quest = QUEST_INDEX.get(selectedId);
    const progress = state.player.quests.find(q => q.questId === selectedId);
    if (quest && progress) {
      let dy = py + 56;
      const dPad = 16;

      // Quest name
      ctx.font = "bold 16px 'Georgia', serif";
      ctx.fillStyle = AMBER;
      ctx.textAlign = "left";
      ctx.fillText(quest.name, detailX + dPad, dy);
      dy += 24;

      // Arc + rarity
      ctx.font = "italic 11px 'Georgia', serif";
      ctx.fillStyle = PARCHMENT_DARK;
      ctx.fillText(`${quest.arc} • ${quest.rarity} • ~${quest.estimatedMinutes} min`, detailX + dPad, dy);
      dy += 20;

      // Description
      ctx.font = "13px 'Georgia', serif";
      ctx.fillStyle = PARCHMENT;
      const descLines = wrapText(ctx, quest.description, panelW - listW - dPad * 2);
      for (const line of descLines.slice(0, 3)) {
        ctx.fillText(line, detailX + dPad, dy);
        dy += 17;
      }
      dy += 8;

      // Steps
      ctx.font = "bold 12px 'Georgia', serif";
      ctx.fillStyle = AMBER;
      ctx.fillText("Objectives:", detailX + dPad, dy);
      dy += 18;

      for (const step of quest.steps) {
        const done = progress.completedSteps.includes(step.id);
        ctx.font = "12px 'Georgia', serif";
        ctx.fillStyle = done ? GREEN_COMPLETE : PARCHMENT_DARK;
        const prefix = done ? "✓  " : "○  ";
        const stepLines = wrapText(ctx, prefix + step.description, panelW - listW - dPad * 2 - 8);
        for (const line of stepLines.slice(0, 2)) {
          ctx.fillText(line, detailX + dPad + 4, dy);
          dy += 16;
        }
        dy += 4;
      }

      // Rewards
      dy += 8;
      ctx.font = "bold 12px 'Georgia', serif";
      ctx.fillStyle = AMBER;
      ctx.fillText("Rewards:", detailX + dPad, dy);
      dy += 18;

      ctx.font = "12px 'Georgia', serif";
      ctx.fillStyle = PARCHMENT;
      const rw = quest.reward;
      if (rw.currency) { ctx.fillText(`${rw.currency} gold`, detailX + dPad + 4, dy); dy += 16; }
      if (rw.reputation) { ctx.fillText(`${rw.reputation} reputation`, detailX + dPad + 4, dy); dy += 16; }
      if (rw.title) { ctx.fillText(`Title: "${rw.title}"`, detailX + dPad + 4, dy); dy += 16; }
    }
  }

  // Close hint
  ctx.font = "10px 'Georgia', serif";
  ctx.fillStyle = "rgba(200, 180, 140, 0.4)";
  ctx.textAlign = "center";
  ctx.fillText("Press L to close", px + panelW / 2, py + panelH - 12);

  void now; // available for animation
  ctx.restore();
}

// ─── Quest Accept/Complete Overlay ──────────────────────────────────────────

/**
 * Draws a brief celebration overlay when a quest is accepted or completed.
 */
export function drawQuestOverlay(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  now: number,
): void {
  const q = state.questUI;
  if (!q.overlayQuestId || !q.overlayAction) return;

  const age = now - q.overlayTime;
  const duration = 2.5;
  if (age > duration) {
    q.overlayQuestId = null;
    q.overlayAction = null;
    return;
  }

  const quest = QUEST_INDEX.get(q.overlayQuestId);
  if (!quest) return;

  const cw = state.canvasWidth;

  // Fade in → hold → fade out
  let alpha: number;
  if (age < 0.3) alpha = age / 0.3;
  else if (age > duration - 0.5) alpha = (duration - age) / 0.5;
  else alpha = 1;

  // Scale pop
  const scalePop = age < 0.2 ? 1 + (0.2 - age) * 2 : 1;

  ctx.save();
  ctx.globalAlpha = alpha;

  const bannerY = 160;

  ctx.save();
  ctx.translate(cw / 2, bannerY);
  ctx.scale(scalePop, scalePop);

  // Banner background
  const bannerW = 360;
  const bannerH = 56;
  roundRect(ctx, -bannerW / 2, -bannerH / 2, bannerW, bannerH, 8);
  ctx.fillStyle = q.overlayAction === "complete" ? "rgba(20, 40, 20, 0.9)" : "rgba(40, 30, 10, 0.9)";
  ctx.fill();
  ctx.strokeStyle = q.overlayAction === "complete" ? GREEN_COMPLETE : AMBER;
  ctx.lineWidth = 2;
  ctx.stroke();

  // Action text
  ctx.font = "bold 12px 'Georgia', serif";
  ctx.fillStyle = q.overlayAction === "complete" ? GREEN_COMPLETE : AMBER;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(
    q.overlayAction === "complete" ? "QUEST COMPLETE" : "QUEST ACCEPTED",
    0, -10,
  );

  // Quest name
  ctx.font = "16px 'Georgia', serif";
  ctx.fillStyle = PARCHMENT;
  ctx.fillText(quest.name, 0, 10);

  ctx.restore();
  ctx.restore();
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const paragraphs = text.split("\n");
  const lines: string[] = [];

  for (const para of paragraphs) {
    if (para.trim() === "") {
      lines.push("");
      continue;
    }
    const words = para.split(" ");
    let line = "";

    for (const word of words) {
      const test = line ? line + " " + word : word;
      if (ctx.measureText(test).width > maxWidth && line) {
        lines.push(line);
        line = word;
      } else {
        line = test;
      }
    }
    if (line) lines.push(line);
  }

  return lines;
}

// ─── DPS Meter Overlay ──────────────────────────────────────────────────────

const METER_W = 200;
const METER_H = 90;

/**
 * Draw the floating DPS meter when attacking a training dummy.
 * Positioned top-center of screen — shows real-time DPS, combo, peak, and biggest hit.
 */
export function drawDPSMeter(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  now: number,
): void {
  const meter = state.trainingDummy?.dpsMeter;
  if (!meter || !meter.visible) return;

  const cw = state.canvasWidth;
  const px = Math.round((cw - METER_W) / 2);
  // Offset below zone banner + mode banner to prevent overlap
  // Zone banner: pillY=14, pillH=32 → bottom at ~46; shifts +44 if mode banner active
  const zoneId = state.world?.currentZoneId;
  const hasZoneBanner = zoneId && zoneId !== "ember_lane";
  const ui = state.ui;
  const hasModeBanner = ui.editMode || ui.combatStance
    || (ui.placingFurnitureType != null) || (ui.movingFurnitureId != null);
  const bannerBottom = hasZoneBanner ? (14 + 32 + (hasModeBanner ? 44 : 0)) : 0;
  const py = Math.max(10, bannerBottom + 6);

  ctx.save();

  // Panel background — dark with amber accent
  roundRect(ctx, px, py, METER_W, METER_H, 8);
  ctx.fillStyle = "rgba(20, 14, 6, 0.85)";
  ctx.fill();
  ctx.strokeStyle = "rgba(232, 176, 64, 0.5)";
  ctx.lineWidth = 1;
  ctx.stroke();

  // Title
  ctx.font = "bold 11px 'Georgia', serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillStyle = AMBER;
  ctx.fillText("⚔ TRAINING METER ⚔", px + METER_W / 2, py + 6);

  // DPS display — large number
  const elapsed = Math.max(0.1, now - meter.startTime);
  const avgDPS = meter.totalDamage / elapsed;
  ctx.font = "bold 22px 'Georgia', serif";
  ctx.fillStyle = meter.currentDPS > meter.peakDPS * 0.8 ? "#ffd040" : PARCHMENT;
  ctx.fillText(`${Math.round(meter.currentDPS)} DPS`, px + METER_W / 2, py + 22);

  // Stats row
  ctx.font = "11px 'Georgia', serif";
  ctx.textAlign = "left";
  ctx.fillStyle = PARCHMENT_DARK;
  const statsY = py + 52;

  // Combo (left)
  ctx.fillText(`Combo: ${meter.combo}`, px + 10, statsY);

  // Biggest hit (center)
  ctx.textAlign = "center";
  ctx.fillText(`Best: ${meter.biggestHit}`, px + METER_W / 2, statsY);

  // Peak DPS (right)
  ctx.textAlign = "right";
  ctx.fillText(`Peak: ${Math.round(meter.peakDPS)}`, px + METER_W - 10, statsY);

  // Total damage bar
  const barY = statsY + 16;
  const barW = METER_W - 20;
  ctx.fillStyle = "rgba(60, 50, 30, 0.6)";
  roundRect(ctx, px + 10, barY, barW, 10, 3);
  ctx.fill();

  // Fill based on avg DPS relative to peak (visual only)
  const fill = meter.peakDPS > 0 ? Math.min(1, meter.currentDPS / meter.peakDPS) : 0;
  if (fill > 0) {
    const grad = ctx.createLinearGradient(px + 10, barY, px + 10 + barW * fill, barY);
    grad.addColorStop(0, "#c08020");
    grad.addColorStop(1, "#ffd040");
    roundRect(ctx, px + 10, barY, Math.max(4, barW * fill), 10, 3);
    ctx.fillStyle = grad;
    ctx.fill();
  }

  // Total damage text
  ctx.font = "9px 'Georgia', serif";
  ctx.textAlign = "center";
  ctx.fillStyle = "rgba(200, 180, 140, 0.6)";
  ctx.fillText(`Total: ${meter.totalDamage} dmg  |  Avg: ${Math.round(avgDPS)} DPS  |  Max combo: ${meter.maxCombo}`, px + METER_W / 2, barY + 12);

  ctx.restore();
}

// ─── Training Dummy Nameplate ───────────────────────────────────────────────

/**
 * Draw a specialized nameplate above a training dummy creature.
 * Shows dummy name, tier icon, and total hits absorbed this session.
 *
 * @param ctx Canvas context
 * @param name Dummy display name
 * @param tier "wooden" | "straw" | "iron" | "armored"
 * @param screenX Screen X center
 * @param screenY Screen Y (above creature head)
 * @param hitsAbsorbed Total hits this session
 * @param now Current time for animations
 */
export function drawDummyNameplate(
  ctx: CanvasRenderingContext2D,
  name: string,
  tier: string,
  screenX: number,
  screenY: number,
  hitsAbsorbed: number,
  now: number,
): void {
  ctx.save();

  // Tier color
  const tierColors: Record<string, string> = {
    wooden: "#c0a060",
    straw: "#d8c060",
    iron: "#a0a8b0",
    armored: "#c08020",
  };
  const tierColor = tierColors[tier] ?? "#a0a0a0";

  // Nameplate background
  const textW = ctx.measureText(name).width || 60;
  const npW = Math.max(80, textW + 30);
  const npH = 20;
  const nx = screenX - npW / 2;
  const ny = screenY - npH;

  roundRect(ctx, nx, ny, npW, npH, 4);
  ctx.fillStyle = "rgba(20, 14, 6, 0.75)";
  ctx.fill();
  ctx.strokeStyle = tierColor;
  ctx.lineWidth = 1;
  ctx.stroke();

  // Tier diamond indicator
  const diaX = nx + 10;
  const diaY = ny + npH / 2;
  ctx.beginPath();
  ctx.moveTo(diaX, diaY - 4);
  ctx.lineTo(diaX + 4, diaY);
  ctx.lineTo(diaX, diaY + 4);
  ctx.lineTo(diaX - 4, diaY);
  ctx.closePath();
  ctx.fillStyle = tierColor;
  ctx.fill();

  // Name text
  ctx.font = "bold 10px 'Georgia', serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = PARCHMENT;
  ctx.fillText(name, screenX, ny + npH / 2);

  // Hits absorbed counter (subtle, right side)
  if (hitsAbsorbed > 0) {
    ctx.font = "9px 'Georgia', serif";
    ctx.textAlign = "right";
    ctx.fillStyle = "rgba(200, 180, 140, 0.5)";
    ctx.fillText(`${hitsAbsorbed}×`, nx + npW - 6, ny + npH / 2);
  }

  ctx.restore();
}
